import React, { useEffect, useLayoutEffect, useState } from "react";
import Layout from "../components/layoutBlack";
import axios from "axios";
import { config } from "../utils/constants";
import { Link, useNavigate, useLocation } from "react-router-dom";
import parse from "html-react-parser";
import Breadcrumbs from '../components/breadcrumbs';
import { Helmet } from "react-helmet-async";


const BlogDetail = () => {
  const REACT_APP_API_URL = config.url.REACT_APP_API_URL;
  const REACT_API_IMAGE_URL = config.url.REACT_API_IMAGE_URL;
  const [blogDetailData, setBlogDetailData] = useState(null);
  const [last4BlogData, setLast4BlogData] = useState([]);
  const navigate = useNavigate();
  
  const query = new URLSearchParams(useLocation().search);
  const location = useLocation();
  const slug = query.get("title");

  useEffect(() => {
    axios
      .post(`${REACT_APP_API_URL}/blog-detail/`, { slug })
      .then((res) => {
        setBlogDetailData(res.data.blog[0]); // Extracting first blog
        setLast4BlogData(res.data.last_4_blogs);
      })
      .catch((err) => console.error("Error fetching blog details", err));
  }, [slug]);

    // Scroll to top on first mount AND whenever the URL changes
  useLayoutEffect(() => {
    // optional: disable browser’s native restoration to avoid fighting us
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // If you use hash anchors on this page, skip auto scroll when hash exists
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [location.pathname, location.search]); // or simply [slug]

  if (!blogDetailData) {
    return <p>Loading...</p>;
  }
  // --- SEO values (after blogDetailData is set) ---
  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = String(html || "");
    return (div.textContent || div.innerText || "").trim();
  };

  const metaTitle =
    (blogDetailData.meta_title && blogDetailData.meta_title.trim()) ||
    blogDetailData.title ||
    "Blog";

  const metaDescription =
    (blogDetailData.meta_description && blogDetailData.meta_description.trim()) ||
    stripHtml(blogDetailData.teaser_text || "")
      .slice(0, 300);

  const metaKeywords = (blogDetailData.meta_keywords || "").trim();

  // image + canonical
  const ogImage = blogDetailData?.og_image ? `${REACT_API_IMAGE_URL}${blogDetailData.og_image}` : null;
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://molu.co";
  const canonical = `${origin}/blog/blog-detail?title=${blogDetailData.slug || slug || ""}`;

  // Optional: BlogPosting JSON-LD
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blogDetailData.title,
    image: ogImage ? [ogImage] : undefined,
    datePublished: blogDetailData.publish_date,
    dateModified: blogDetailData.updated_at || blogDetailData.publish_date,
    description: metaDescription,
    mainEntityOfPage: canonical,
    publisher: {
      "@type": "Organization",
      name: "Molu",
    },
  };
  return (
    <Layout>
      <Helmet prioritizeSeoTags>
        <title>{metaTitle}</title>
        <meta key="desc" name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />

        <meta key="og:type" property="og:type" content="article" />
        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        {ogImage && <meta key="og:image" property="og:image" content={ogImage} />}
        <meta key="og:url" property="og:url" content={canonical} />
        <meta key="fb:app_id" property="fb:app_id" content="1514691803168501" />

        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta key="twitter:title" name="twitter:title" content={metaTitle} />
        <meta key="twitter:description" name="twitter:description" content={metaDescription} />
        {ogImage && <meta key="twitter:image" name="twitter:image" content={ogImage} />}
      </Helmet>
      <section className="bg0 empty-top" >

      </section>

      <div className="container p-b-10">
      <Breadcrumbs />
      </div>

      {/* Blog Detail Section */}
      <section className="full-page-section bg0 p-t-42 p-b-20">
        <div className="container">
          <div className="row">
            <div className="col-md-8 col-lg-9 p-b-80">
              <div className="p-r-45 p-r-0-lg">
                {/* Blog Image */}
                <div className="wrap-pic-w how-pos5-parent">
                  <img
                    src={`${REACT_API_IMAGE_URL}${blogDetailData.lead_image}`}
                    alt={blogDetailData.title}
                  />
                  <div className="flex-col-c-m size-123 bg9 how-pos5">
                    <span className="ltext-107 cl2 txt-center">
                      {new Date(blogDetailData.publish_date).getDate()}
                    </span>
                    <span className="stext-109 cl3 txt-center">
                      {new Date(blogDetailData.publish_date).toLocaleString(
                        "default",
                        { month: "short", year: "numeric" }
                      )}
                    </span>
                  </div>
                </div>

                {/* Blog Content */}
                <div className="p-t-32">
                  <h4 className="ltext-109 cl2 p-b-28">{blogDetailData.title}</h4>
                  <p className="stext-117 cl6 p-b-26">{parse(blogDetailData.teaser_text)}</p>
                  {[
                  ...(blogDetailData?.paragraph || []).map((p, i) => ({
                    key: `p-${p.id ?? i}`,
                    order: p.order ?? 0,
                    type: p.content_type ?? "body-text",
                    content: p.content,
                  })),
                  ...(blogDetailData?.image || []).map((img, i) => ({
                    key: `img-${img.id ?? i}`,
                    order: img.order ?? 0,
                    type: img.content_type ?? "image",
                    src: img.image, // relative path from API
                    alt: blogDetailData?.title || "Blog image",
                  })),
                ]
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((block) => {
                    if (block.type === "image") {
                      return (
                        <div key={block.key} className="p-b-26">
                          <img
                            className="img-fluid"
                            src={`${REACT_API_IMAGE_URL}${block.src}`}
                            alt={block.alt}
                            loading="lazy"
                            style={{ width: "100%", height: "auto", borderRadius: 12 }}
                          />
                        </div>
                      );
                    }

                    return (
                      <div key={block.key} className="stext-117 cl6 p-b-26">
                        {parse(block.content || "")}
                      </div>
                    );
                  })}
                </div>

                {/* Tags */}
                <div className="flex-w flex-t p-t-16">
                  <span className="size-216 stext-116 cl8 p-t-4">Tags</span>
                  <div className="flex-w size-217">
                    {blogDetailData.meta_keywords.split(",").map((tag, index) => (
                      <a
                        key={index}
                        href="/"
                        className="flex-c-m stext-107 cl6 size-301 bor7 p-lr-15 hov-tag1 trans-04 m-r-5 m-b-5"
                      >
                        {tag.trim()}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-md-4 col-lg-3 p-b-80">
              <div className="side-menu">
                {/* Search Bar */}
                <div className="bor17 of-hidden pos-relative">
                  <input
                    className="stext-103 cl2 plh4 size-116 p-l-28 p-r-55"
                    type="text"
                    name="search"
                    placeholder="Search"
                  />
                  <button className="flex-c-m size-122 ab-t-r fs-18 cl4 hov-cl1 trans-04">
                    <i className="zmdi zmdi-search"></i>
                  </button>
                </div>

                {/* Last 4 Blogs */}
                <div className="p-t-55">
                  <h4 className="mtext-112 cl2 p-b-33">Recent Posts</h4>
                  <ul>
                    {last4BlogData.map((blog) => (
                      <li className="bor18" key={blog.id}>
                        <Link
                          to={`/blog/blog-detail?title=${blog.slug}`}
                          className="dis-block stext-115 cl6 hov-cl1 trans-04 p-tb-8 p-lr-4"
                        >
                          {blog.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Tags */}
                <div className="p-t-50">
                  <h4 className="mtext-112 cl2 p-b-27">Tags</h4>
                  <div className="flex-w m-r--5">
                    {blogDetailData.meta_keywords.split(",").map((tag, index) => (
                      <a
                        key={index}
                        href="/"
                        className="flex-c-m stext-107 cl6 size-301 bor7 p-lr-15 hov-tag1 trans-04 m-r-5 m-b-5"
                      >
                        {tag.trim()}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogDetail;
